<template>
  <v-form>
    <v-row justify="center">
      <v-col cols="12" sm="9" xl="4">
        <div class="cropper-wrapper">
          <PhotoCropper
            ref="cropper"
            :imageUrl="imageUrl"
            :imageLoader="imageLoader"
            header-tooltip="message.posts.addImageTooltip"
            @upload-image="uploadImage"
            @delete="handleDeleteImage"
          />
        </div>
        <v-text-field
          v-model="form.event.title"
          counter="58"
          v-validate="'required|max:58'"
          :data-vv-name="$t('message.posts.offerName')"
          :error-messages="errors.collect($t('message.posts.offerName'))"
          :label="`${$t('message.posts.offerName')} *`"
          required
        ></v-text-field>

        <v-col cols="5" offset-lg="8" class="pt-0 pb-0">
          <v-switch
            inset
            color="primary"
            v-model="addTime"
            class="mt-0 -mt-4"
            hide-details
            :label="$t('message.posts.addTime')"
          ></v-switch>
        </v-col>

        <v-row>
          <v-col :cols="addTime ? 7 : 12">
            <v-menu
              ref="startDateMenu"
              v-model="menu.startDate"
              :close-on-content-click="false"
              :return-value.sync="form.event.schedule.start_date"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="form.event.schedule.start_date"
                  v-validate="'required'"
                  :data-vv-name="$t('message.posts.startDate')"
                  :error-messages="
                    errors.collect($t('message.posts.startDate'))
                  "
                  :label="`${$t('message.posts.startDate')} *`"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  required
                ></v-text-field>
              </template>

              <v-date-picker
                v-model="form.event.schedule.start_date"
                :locale="selectedLocale"
                type="date"
                no-title
                scrollable
                color="primary"
                eventColor="primary"
              >
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="menu.startDate = false"
                  >{{ $t("message.common.cancel") }}
                </v-btn>
                <v-btn
                  text
                  color="primary"
                  @click="
                    $refs.startDateMenu.save(form.event.schedule.start_date)
                  "
                  >{{ $t("message.common.ok") }}
                </v-btn>
              </v-date-picker>
            </v-menu>
            <v-menu
              ref="endDateMenu"
              v-model="menu.endDate"
              :close-on-content-click="false"
              :return-value.sync="form.event.schedule.end_date"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="form.event.schedule.end_date"
                  v-validate="'required'"
                  :data-vv-name="$t('message.posts.endDate')"
                  :error-messages="errors.collect($t('message.posts.endDate'))"
                  :label="`${$t('message.posts.endDate')} *`"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  required
                  :disabled="!form.event.schedule.start_date"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="form.event.schedule.end_date"
                :locale="selectedLocale"
                type="date"
                :min="form.event.schedule.start_date"
                no-title
                scrollable
                color="primary"
                eventColor="primary"
              >
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="menu.endDate = false"
                  >{{ $t("message.common.cancel") }}
                </v-btn>
                <v-btn
                  text
                  color="primary"
                  @click="$refs.endDateMenu.save(form.event.schedule.end_date)"
                  >{{ $t("message.common.ok") }}
                </v-btn>
              </v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="5" v-if="addTime">
            <v-menu
              ref="startTimeMenu"
              v-model="menu.startTime"
              :close-on-content-click="false"
              :nudge-right="40"
              :return-value.sync="form.event.schedule.start_time"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="form.event.schedule.start_time"
                  name="startTime"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  v-validate="{ required: addTime }"
                  :data-vv-name="$t('message.posts.startTime')"
                  :error-messages="
                    errors.collect($t('message.posts.startTime'))
                  "
                  :label="`${$t('message.posts.startTime')} *`"
                  required
                ></v-text-field>
              </template>
              <v-time-picker
                v-if="menu.startTime"
                format="24hr"
                color="primary"
                v-model="form.event.schedule.start_time"
                full-width
                @click:minute="
                  $refs.startTimeMenu.save(form.event.schedule.start_time)
                "
              ></v-time-picker>
            </v-menu>
            <v-menu
              ref="endTimeMenu"
              v-model="menu.endTime"
              :close-on-content-click="false"
              :nudge-right="40"
              :return-value.sync="form.event.schedule.end_time"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="form.event.schedule.end_time"
                  name="endTime"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  v-validate="'required'"
                  :data-vv-name="$t('message.posts.endTime')"
                  :error-messages="errors.collect($t('message.posts.endTime'))"
                  :label="`${$t('message.posts.endTime')} *`"
                  :disabled="!form.event.schedule.start_time"
                  required
                ></v-text-field>
              </template>
              <v-time-picker
                v-if="menu.endTime"
                format="24hr"
                color="primary"
                v-model="form.event.schedule.end_time"
                full-width
                :min="
                  form.event.schedule.start_date ===
                  form.event.schedule.end_date
                    ? form.event.schedule.start_time
                    : null
                "
                @click:minute="
                  $refs.endTimeMenu.save(form.event.schedule.end_time)
                "
              ></v-time-picker>
            </v-menu>
          </v-col>
        </v-row>
        <v-checkbox
          v-model="showExtra"
          :label="$t('message.posts.showExtra')"
        ></v-checkbox>

        <template v-if="showExtra">
          <v-textarea
            name="text"
            v-model="form.text"
            v-validate="{ required: showExtra }"
            :data-vv-name="$t('message.posts.offerDetails')"
            :error-messages="errors.collect($t('message.posts.offerDetails'))"
            :label="`${$t('message.posts.offerDetails')} *`"
            required
            rows="2"
            auto-grow
          ></v-textarea>
          <v-text-field
            name="coupon_code"
            v-model="form.offer.coupon_code"
            :label="$t('message.posts.offerCode')"
          ></v-text-field>
          <v-text-field
            name="redeem_online_url"
            v-model="form.offer.redeem_online_url"
            v-validate="{ url: true }"
            :data-vv-name="$t('message.posts.offerLink')"
            :error-messages="errors.collect($t('message.posts.offerLink'))"
            :label="$t('message.posts.offerLink')"
          ></v-text-field>
          <v-text-field
            name="terms_conditions"
            v-model="form.offer.terms_conditions"
            :label="$t('message.posts.offerTerms')"
          ></v-text-field>
        </template>
      </v-col>
    </v-row>
    <v-row justify="center">
      <v-col cols="12" sm="9" xl="4">
        <PublicationDatePicker
          v-if="canPlanMarketingPublications"
          :type="type"
          :options="PUBLICATION_DATE_OPTIONS"
          :title="$t('message.posts.planning.publication.publicationDateTitle')"
          :subtitle="
            $t('message.posts.planning.publication.publicationDateSubtitle')
          "
          :edit-mode="editMode"
          :publish-at="form.publish_at"
          @change="handlePublicationDateChange"
        />
      </v-col>
    </v-row>
    <v-row justify="center" class="mt-4" no-gutters>
      <Footer>
        <ui-button
          color="primary"
          class="mr-8"
          @click="submit"
          :disabled="btnsDisabled"
        >
          {{ buttonLabel }}
        </ui-button>
        <ui-button
          outlined
          color="primary"
          :disabled="btnsDisabled"
          icon="icon-search"
          @click="showPreview()"
        >
          {{ $t("message.posts.preview") }}
        </ui-button>
      </Footer>
    </v-row>
  </v-form>
</template>

<script>
import { debounce } from "@/helpers/helpers";
import { mapState } from "vuex";
import moment from "moment";
import cloneDeep from "lodash/cloneDeep";

import PhotoCropper from "@/components/Photos/Cropper.vue";
import { postFormMixin } from "@/helpers/mixins/post-form";
export default {
  name: "PostsOfferForm",
  model: {
    event: "setForm"
  },

  mixins: [postFormMixin],

  props: {
    imageLoader: {
      type: Boolean,
      required: true
    },
    imageUrl: {
      type: String,
      required: false
    },
    value: { type: Object },
    editMode: {
      default: false,
      type: Boolean
    }
  },

  data() {
    return {
      addTime: false,
      showExtra: false,
      menu: {
        startDate: false,
        endDate: false,
        startTime: false,
        endTime: false
      },
      form: null
    };
  },

  components: { PhotoCropper },
  computed: {
    ...mapState({
      selectedLocale: state => state.settings.selectedLocale
    }),
    btnsDisabled() {
      const editMode = this.$refs.cropper ? this.$refs.cropper.editMode : false;
      return this.imageLoader || editMode;
    }
  },

  created() {
    this.resetForm();
  },

  watch: {
    form: {
      handler: debounce(function(val) {
        this.setForm(val);
      }, 500),
      deep: true
    },

    showExtra: function(val) {
      if (val === false) {
        this.form.text = null;
        this.form.offer = {
          coupon_code: null,
          redeem_online_url: null,
          terms_conditions: null
        };
      }
    }
  },

  methods: {
    setForm(val) {
      let formData = cloneDeep(val);
      if (
        formData.text ||
        (formData.call_to_action && formData.call_to_action.type)
      ) {
        this.showExtra = true;
      }
      this.setDateAndTime(formData);
      this.$emit("setForm", formData);
    },
    setFormPreset(form) {
      form.offer = {
        coupon_code: (form.offer && form.offer.couponCode) || null,
        redeem_online_url: (form.offer && form.offer.redeemOnlineUrl) || null,
        terms_conditions: (form.offer && form.offer.termsConditions) || null
      };

      if (!form.call_to_action) {
        form.call_to_action = {
          type: null,
          url: null
        };
      }
      return form;
    },
    resetForm() {
      this.form = this.value.id
        ? this.setFormPreset(this.value)
        : {
            text: null,
            event: {
              title: null,
              schedule: {
                start_date: null,
                start_time: null,
                end_date: null,
                end_time: null
              }
            },
            offer: {
              coupon_code: null,
              redeem_online_url: null,
              terms_conditions: null
            },
            publish_at: null
          };
    }
  }
};
</script>

<style lang="scss" scoped>
.-mt-4 {
  margin-left: $spacing * -2;
}

.cropper-wrapper {
  width: 100%;
  min-height: 250px;
  display: flex;
}
</style>
